import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from 'react-oidc-context'

import { getQueryClient } from 'Common/base/queryClient'
import { Environment } from 'Common/environment/types'
import queryKeys from 'Common/queryKeys/queryKeys'

import { AccessMap, getUserData } from './authHelper'
import { getUserAccess } from './getUserAccess'
import { Authentication, initialAuth } from './types'

type SidAuthBridgeProps = {
    environment: Environment
    children: (logout: () => void) => React.ReactNode
}

const currentPath = () => `${window.location.pathname}${window.location.search || ''}`

const SidAuthBridge = ({ children }: SidAuthBridgeProps) => {
    const auth = useAuth()
    const navigate = useNavigate()
    const [authReady, setAuthReady] = useState(false)
    const restored = useRef(false)
    const [startupRenewSettled, setStartupRenewSettled] = useState(false)
    const startupRenewTried = useRef(false)
    const authRef = useRef(auth)
    const navigateRef = useRef(navigate)
    const redirectingToLogin = useRef(false)
    const renewFailed = useRef(false)

    authRef.current = auth
    navigateRef.current = navigate

    const goToLogin = useCallback(() => {
        if (redirectingToLogin.current) return
        redirectingToLogin.current = true
        authRef.current.signinRedirect({ state: currentPath() }).catch((error) => {
            redirectingToLogin.current = false
            console.warn('[SID] could not redirect to login', error)
        })
    }, [])

    const markRenewFailed = useCallback(() => {
        if (renewFailed.current) return
        renewFailed.current = true
        try {
            authRef.current.stopSilentRenew()
        } catch (error) {
            console.warn('[SID] could not stop silent renew', error)
        }
        authRef.current
            .removeUser()
            .catch((error) => console.warn('[SID] could not clear expired session', error))
    }, [])

    const renewOrGoToLogin = useCallback(async () => {
        if (renewFailed.current) {
            goToLogin()
            return
        }
        // useAuth() swallows navigator errors and resolves with null, so a
        // missing user is the only signal that the renewal failed.
        const renewed = await authRef.current.signinSilent().catch(() => null)
        if (!renewed) {
            markRenewFailed()
            goToLogin()
        }
    }, [goToLogin, markRenewFailed])

    useEffect(() => {
        const removeRenewError = auth.events.addSilentRenewError(() => {
            markRenewFailed()
            goToLogin()
        })
        const removeExpired = auth.events.addAccessTokenExpired(() => {
            void renewOrGoToLogin()
        })
        return () => {
            removeRenewError()
            removeExpired()
        }
    }, [auth.events, goToLogin, markRenewFailed, renewOrGoToLogin])

    useEffect(() => {
        const revalidate = () => {
            if (document.visibilityState !== 'visible') return
            const user = authRef.current.user
            if (!user?.expired) return
            void renewOrGoToLogin()
        }

        document.addEventListener('visibilitychange', revalidate)
        window.addEventListener('focus', revalidate)
        return () => {
            document.removeEventListener('visibilitychange', revalidate)
            window.removeEventListener('focus', revalidate)
        }
    }, [renewOrGoToLogin])

    useEffect(() => {
        if (auth.isLoading || auth.activeNavigator) return
        if (startupRenewTried.current) return
        startupRenewTried.current = true

        if (renewFailed.current) {
            setStartupRenewSettled(true)
            return
        }

        if (!auth.user) {
            setStartupRenewSettled(true)
            return
        }

        auth.signinSilent()
            .catch(() => null)
            .then((renewed) => {
                if (!renewed) {
                    markRenewFailed()
                    console.warn('[SID] startup silent renew failed')
                }
            })
            .finally(() => setStartupRenewSettled(true))
    }, [auth, markRenewFailed])

    useEffect(() => {
        if (auth.isLoading || auth.activeNavigator || auth.isAuthenticated) return
        if (!startupRenewSettled) return

        goToLogin()
    }, [
        auth.isAuthenticated,
        auth.isLoading,
        auth.activeNavigator,
        auth.error,
        startupRenewSettled,
        goToLogin,
    ])

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.user) {
            setAuthReady(false)
            return
        }

        let cancelled = false
        const user = auth.user

        const finish = (publishingHouses?: unknown, access?: AccessMap) => {
            if (cancelled) return
            const userData = getUserData(user, publishingHouses, access)
            console.info('[SID] signed in user', {
                profile: user.profile,
                scopes: user.scope,
                expiresAt: user.expires_at,
                userData,
            })
            getQueryClient().setQueryData<Authentication>([queryKeys.auth], {
                ...initialAuth,
                isAuthenticated: true,
                data: userData,
            })
            setAuthReady(true)

            if (!restored.current && typeof user.state === 'string') {
                restored.current = true
                navigateRef.current({ href: user.state })
            }
        }

        getUserAccess(user.access_token).then((access) => finish(undefined, access))

        return () => {
            cancelled = true
        }
        // keyed on the access token so this runs on sign-in and on token refresh only,
        // not on every navigation (useNavigate returns a new function each time)
    }, [auth.isAuthenticated, auth.user?.access_token])

    if ((auth.activeNavigator || auth.isLoading) && !auth.isAuthenticated) {
        return null
    }

    if (!auth.isAuthenticated) {
        if (auth.error) console.warn('[SID] auth error', auth.error)
        return null
    }

    if (!authReady) {
        return null
    }

    return <>{children(() => auth.signoutRedirect())}</>
}

export default SidAuthBridge
